import React from "react";
import { MapPin, Navigation, Apple } from "lucide-react";
import { useApp } from "../context/AppContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

/**
 * Clickable job address. Tapping it opens a small menu with
 * "Google Maps" and "Waze" (universal links — open the native apps when
 * installed). Safe to use inside clickable cards: clicks never bubble up.
 */
export const AddressLink = ({ address, city, className = "", iconClass = "h-3 w-3", showIcon = true, children }) => {
  const { t } = useApp();
  // PMHR addresses often ALREADY contain the city/state — never duplicate it
  // (e.g. "1974 Canfield Gln, Lawrenceville, Georgia 30044 United States" + "Lawrenceville 30044")
  const cityName = (city || "").replace(/\b\d{5}(?:-\d{4})?\b/g, "").trim();
  const hasCityAlready = cityName && (address || "").toLowerCase().includes(cityName.toLowerCase());
  const full = hasCityAlready ? address : [address, city].filter(Boolean).join(", ");
  if (!full) return null;
  const q = encodeURIComponent(full);
  const stop = (e) => e.stopPropagation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="address-link"
          onClick={stop}
          className={`inline-flex items-center gap-1 min-w-0 max-w-full overflow-hidden text-left underline decoration-dotted underline-offset-2 hover:text-primary focus-visible:text-primary transition-colors ${className}`}
          title={t("open_in_maps")}
          aria-label={`${t("open_in_maps")}: ${full}`}
        >
          {showIcon && <MapPin className={`${iconClass} shrink-0`} />}
          <span className="truncate min-w-0">{children || full}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={stop} className="min-w-[200px]">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground max-w-[260px] truncate">
          {full}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="address-open-apple-maps">
          <a
            href={`https://maps.apple.com/?q=${q}`}
            target="_blank"
            rel="noreferrer"
            onClick={stop}
            className="cursor-pointer"
          >
            <Apple className="h-4 w-4 mr-2 text-primary" /> Apple Maps
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="address-open-google-maps">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${q}`}
            target="_blank"
            rel="noreferrer"
            onClick={stop}
            className="cursor-pointer"
          >
            <MapPin className="h-4 w-4 mr-2 text-primary" /> Google Maps
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="address-open-waze">
          <a
            href={`https://waze.com/ul?q=${q}&navigate=yes`}
            target="_blank"
            rel="noreferrer"
            onClick={stop}
            className="cursor-pointer"
          >
            <Navigation className="h-4 w-4 mr-2 text-primary" /> Waze
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
